// src/services/donationService.ts
import type {
  Donor,
  Donation,
  DonationStatus,
  DonationListResponse,
  DonorListResponse,
  GetDonationsParams
} from "../../types/donation";
import { awsConfig } from "../config/aws-config";
import { toast } from "react-toastify"; // For user notifications
import { Metrics } from "../../types/metrics";
import { getCachedDonations, setCachedDonations, clearCachedDonations } from "../../cache/donationCache";

// Interfaces
interface CreateCheckoutSessionPayload {
  firstName?: string;
  lastName?: string;
  email: string;
  amount: number;
  currency?: string;
  isRecurring?: boolean;
}

interface CreateCheckoutSessionResponse {
  url: string; // Stripe Checkout URL
}

interface UpdateDonationStatusPayload {
  status: DonationStatus;
  reason?: string;
  notes?: string;
}

export const donationService = {
  /**
   * Creates a Stripe Checkout Session.
   * Endpoint: POST /donations/checkout
   */
  createCheckoutSession: async (
    data: CreateCheckoutSessionPayload,
  ): Promise<string> => {
    const url = `${awsConfig.apiEndpoint}/donations/checkout`;
   ////console.log("Creating checkout session with data:", data, "at URL:", url);

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log("Creating checkout session with data:", data);
      }

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to create checkout session");
      }

      const responseData: CreateCheckoutSessionResponse = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log("Received checkout session response:", responseData);
      }

      return responseData.url;
    } catch (error: any) {
      console.error("Error creating checkout session:", error);
      toast.error(error.message || "Failed to create checkout session");
      throw error;
    }
  },

  /**
   * Retrieves donations with optional filtering and pagination.
   * Uses cache to avoid unnecessary API calls.
   * Endpoint: GET /admin/donations
   */
  getDonations: async (
    token: string,
    params: GetDonationsParams = {},
    forceRefresh: boolean = false
  ): Promise<DonationListResponse> => {
    // Check if valid cached data exists and we're not forcing a refresh
    const cached = !forceRefresh ? getCachedDonations() : null;
    if (cached) {
      if (process.env.NODE_ENV === "development") {
        //console.log("Returning cached donation data");
      }
      return cached;
    }

    // Build query string from parameters.
    const baseUrl = `${awsConfig.apiEndpoint}/admin/donations`;
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.startKey) queryParams.append("startKey", params.startKey);
    if (params.status) queryParams.append("status", params.status);
    if (params.donorId) queryParams.append("donorId", params.donorId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);
    if (params.minAmount !== undefined) queryParams.append("minAmount", params.minAmount.toString());
    if (params.maxAmount !== undefined) queryParams.append("maxAmount", params.maxAmount.toString());

    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
    //console.log(`Fetching donations from ${url} ${forceRefresh ? "(forced refresh)" : ""}`);

    try {
      if (process.env.NODE_ENV === "development") {
        //console.log("Fetching donations with filters and pagination.");
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch donations");
      }

      const data: DonationListResponse = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log("Received donations response:", data);
      }

      // Cache the fresh donation data.
      setCachedDonations(data);

      return data;
    } catch (error: any) {
      console.error("Error fetching donations:", error);
      toast.error(error.message || "Failed to fetch donations");
      throw error;
    }
  },

  /**
   * Retrieves a single donation by ID (ADMIN).
   * Endpoint: GET /admin/donations/{id}
   */
  getDonationById: async (
    token: string,
    donationId: string
  ): Promise<Donation> => {
    const url = `${awsConfig.apiEndpoint}/admin/donations/${donationId}`;

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log(`Fetching donation with ID: ${donationId}`);
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch donation");
      }

      const data: { donation: Donation } = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log(`Received donation data for ID ${donationId}:`, data);
      }

      return data.donation;
    } catch (error: any) {
      console.error(`Error fetching donation with ID ${donationId}:`, error);
      toast.error(error.message || "Failed to fetch donation");
      throw error;
    }
  },

  /**
   * Updates the status of a donation (ADMIN).
   * Endpoint: PUT /admin/donations/{id}/status
   */
  updateDonationStatus: async (
    token: string,
    donationId: string,
    data: UpdateDonationStatusPayload
  ): Promise<Donation> => {
    const url = `${awsConfig.apiEndpoint}/admin/donations/${donationId}/status`;

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log(`Updating status for donation ID ${donationId} with data:`, data);
      }

      const response = await fetch(url, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to update donation status");
      }

      const responseData: { success: boolean; donation: Donation } = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log(`Received update response for donation ID ${donationId}:`, responseData);
      }

      // Clear the donation cache so that subsequent reads fetch fresh data.
      clearCachedDonations();

      return responseData.donation;
    } catch (error: any) {
      console.error(`Error updating donation status for ID ${donationId}:`, error);
      toast.error(error.message || "Failed to update donation status");
      throw error;
    }
  },

  /**
   * Retrieves donors with optional filtering and pagination.
   * Endpoint: GET /admin/donors
   */
  getDonors: async (
    token: string,
    params: GetDonationsParams = {} // Reusing the same params interface for simplicity
  ): Promise<DonorListResponse> => {
    const baseUrl = `${awsConfig.apiEndpoint}/admin/donors`;
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.startKey) queryParams.append("startKey", params.startKey);
    if (params.status) queryParams.append("status", params.status);
    if (params.donorId) queryParams.append("donorId", params.donorId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);
    if (params.minAmount !== undefined) queryParams.append("minAmount", params.minAmount.toString());
    if (params.maxAmount !== undefined) queryParams.append("maxAmount", params.maxAmount.toString());

    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
   ////console.log("Fetching donors with parameters:", url);

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log("Fetching donors with filters and pagination.");
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch donors");
      }

      const data: DonorListResponse = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log("Received donors response:", data);
      }

      return data;
    } catch (error: any) {
      console.error("Error fetching donors:", error);
      toast.error(error.message || "Failed to fetch donors");
      throw error;
    }
  },

  /**
   * Retrieves a single donor by ID (ADMIN).
   * Endpoint: GET /admin/donors/{id}
   */
  getDonorById: async (
    token: string,
    donorId: string
  ): Promise<Donor> => {
   ////console.log("Donors API call triggered:");

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log(`Fetching donor with ID: ${donorId}`);
      }

      // Replace 'url' with the actual endpoint when available.
      const response = await fetch("url", {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch donor");
      }

      const data: { donor: Donor } = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log(`Received donor data for ID ${donorId}:`, data);
      }

      return data.donor;
    } catch (error: any) {
      console.error(`Error fetching donor with ID ${donorId}:`, error);
      toast.error(error.message || "Failed to fetch donor");
      throw error;
    }
  },

  /**
   * Retrieves comprehensive dashboard metrics.
   * Endpoint: GET /admin/dashboard/metrics
   */
  fetchMetrics: async (
    token: string
  ): Promise<Metrics> => {
    const url = `${awsConfig.apiEndpoint}/admin/dashboard/metrics`;

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log("Fetching dashboard metrics from:", url);
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch metrics");
      }

      const data: Metrics = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log("Received metrics data:", data);
      }

      return data;
    } catch (error: any) {
      console.error("Error fetching metrics:", error);
      toast.error(error.message || "Failed to fetch metrics");
      throw error;
    }
  },

  /**
   * Retrieves donations for a specific donor with optional pagination.
   * Endpoint: GET /admin/donors/{id}/donations
   */
  getDonorDonations: async (
    token: string,
    donorId: string,
    params: GetDonationsParams = {}
  ): Promise<DonationListResponse> => {
    const baseUrl = `${awsConfig.apiEndpoint}/admin/donors/${donorId}/donations`;
    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append("limit", params.limit.toString());
    if (params.startKey) queryParams.append("startKey", params.startKey);
    if (params.status) queryParams.append("status", params.status);
    if (params.donorId) queryParams.append("donorId", params.donorId);
    if (params.startDate) queryParams.append("startDate", params.startDate);
    if (params.endDate) queryParams.append("endDate", params.endDate);
    if (params.minAmount !== undefined) queryParams.append("minAmount", params.minAmount.toString());
    if (params.maxAmount !== undefined) queryParams.append("maxAmount", params.maxAmount.toString());

    const url = queryParams.toString() ? `${baseUrl}?${queryParams.toString()}` : baseUrl;
   ////console.log("Fetching donor donations with parameters:", url);

    try {
      if (process.env.NODE_ENV === "development") {
       ////console.log(`Fetching donations for donor ID ${donorId} with filters and pagination.`);
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        mode: "cors",
      });

      if (!response.ok) {
        const errorData = await parseErrorResponse(response);
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to fetch donor donations");
      }

      const data: DonationListResponse = await response.json();

      if (process.env.NODE_ENV === "development") {
       ////console.log(`Received donations for donor ID ${donorId}:`, data);
      }

      return data;
    } catch (error: any) {
      console.error(`Error fetching donations for donor ${donorId}:`, error);
      toast.error(error.message || "Failed to fetch donor donations");
      throw error;
    }
  },
};

/**
 * Parses the error response from the API.
 * @param response - The fetch Response object.
 * @returns Parsed error data.
 */
const parseErrorResponse = async (response: Response): Promise<{ message: string }> => {
  try {
    const data = await response.json();
    return { message: data.message || "An error occurred" };
  } catch {
    return { message: response.statusText || "An error occurred" };
  }
};
